// src/components/ui/calendar/navigation/CalendarFilters/CalendarFilters.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Filter, ChevronDown, X } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { EventStatusBadge } from '../../../ui';

const CalendarFilters = ({
  // Filter state
  statusFilter = '',
  categoryFilter = '',
  customerFilter = '',
  
  // Filter handlers
  onStatusFilterChange,
  onCategoryFilterChange,
  onCustomerFilterChange,
  onClearFilters,
  
  // Data for filter options
  availableStatuses = ['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'],
  availableCategories = ['service', 'general'],
  availableCustomers = [],
  
  // UI options
  compact = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Count active filters
  const activeFilterCount = [statusFilter, categoryFilter, customerFilter].filter(Boolean).length;

  // Clear all filters
  const handleClearAll = () => {
    onStatusFilterChange?.('');
    onCategoryFilterChange?.('');
    onCustomerFilterChange?.('');
    onClearFilters?.();
  };

  if (compact) {
    // Compact mode: Just filter icon with count
    return (
      <div className={cn("relative", className)}>
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "p-2 rounded-lg border transition-all duration-200",
            "hover:scale-105 active:scale-95",
            activeFilterCount > 0
              ? "bg-blue-500/20 border-blue-500/30 text-blue-300"
              : "bg-slate-800/30 border-slate-600/30 text-slate-400 hover:text-slate-200"
          )}
        >
          <div className="relative">
            <Filter className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                {activeFilterCount}
              </div>
            )}
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full right-0 mt-2 w-64 bg-slate-800 border border-slate-700/50 rounded-xl shadow-xl z-50"
          >
            <FilterContent
              statusFilter={statusFilter}
              categoryFilter={categoryFilter}
              customerFilter={customerFilter}
              onStatusFilterChange={onStatusFilterChange}
              onCategoryFilterChange={onCategoryFilterChange}
              onCustomerFilterChange={onCustomerFilterChange}
              availableStatuses={availableStatuses}
              availableCategories={availableCategories}
              availableCustomers={availableCustomers}
              onClearAll={handleClearAll}
              activeFilterCount={activeFilterCount}
              onClose={() => setIsOpen(false)}
            />
          </div>
        )}
      </div>
    );
  }

  // Full mode: Expanded filter controls
  return (
    <div className={cn(
      "bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4",
      className
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-medium text-slate-300">Filters</h3>
          {activeFilterCount > 0 && (
            <div className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
              {activeFilterCount} active
            </div>
          )}
        </div>
        
        {activeFilterCount > 0 && (
          <button
            onClick={handleClearAll}
            className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      <FilterContent
        statusFilter={statusFilter}
        categoryFilter={categoryFilter}
        customerFilter={customerFilter}
        onStatusFilterChange={onStatusFilterChange}
        onCategoryFilterChange={onCategoryFilterChange}
        onCustomerFilterChange={onCustomerFilterChange}
        availableStatuses={availableStatuses}
        availableCategories={availableCategories}
        availableCustomers={availableCustomers}
        expanded={true}
      />
    </div>
  );
};

// Shared filter content component
const FilterContent = ({
  statusFilter,
  categoryFilter,
  customerFilter,
  onStatusFilterChange,
  onCategoryFilterChange,
  onCustomerFilterChange,
  availableStatuses,
  availableCategories,
  availableCustomers,
  onClearAll,
  activeFilterCount,
  onClose,
  expanded = false
}) => {
  return (
    <div className={cn("space-y-4", !expanded && "p-4")}>
      {/* Status Filter */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-2">Status</label>
        <div className="space-y-2">
          <button
            onClick={() => onStatusFilterChange?.('')}
            className={cn(
              "w-full text-left px-2 py-1 rounded text-sm transition-colors",
              !statusFilter 
                ? "bg-blue-500/20 text-blue-300" 
                : "text-slate-300 hover:bg-slate-700/30"
            )}
          >
            All Statuses
          </button>
          {availableStatuses.map(status => (
            <button
              key={status}
              onClick={() => onStatusFilterChange?.(status)}
              className={cn(
                "w-full text-left px-2 py-1 rounded text-sm transition-colors flex items-center gap-2",
                statusFilter === status 
                  ? "bg-blue-500/20 text-blue-300" 
                  : "text-slate-300 hover:bg-slate-700/30"
              )}
            >
              <EventStatusBadge status={status} size="xs" variant="icon-only" />
              <span className="capitalize">{status.replace('_', ' ')}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-2">Category</label>
        <div className="space-y-2">
          <button
            onClick={() => onCategoryFilterChange?.('')}
            className={cn(
              "w-full text-left px-2 py-1 rounded text-sm transition-colors",
              !categoryFilter 
                ? "bg-blue-500/20 text-blue-300" 
                : "text-slate-300 hover:bg-slate-700/30"
            )}
          >
            All Categories
          </button>
          {availableCategories.map(category => (
            <button
              key={category}
              onClick={() => onCategoryFilterChange?.(category)}
              className={cn(
                "w-full text-left px-2 py-1 rounded text-sm transition-colors",
                categoryFilter === category 
                  ? "bg-blue-500/20 text-blue-300" 
                  : "text-slate-300 hover:bg-slate-700/30"
              )}
            >
              <span className="capitalize">{category}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Customer Filter (if customers available) */}
      {availableCustomers.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-2">Customer</label>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            <button
              onClick={() => onCustomerFilterChange?.('')}
              className={cn(
                "w-full text-left px-2 py-1 rounded text-sm transition-colors",
                !customerFilter 
                  ? "bg-blue-500/20 text-blue-300" 
                  : "text-slate-300 hover:bg-slate-700/30"
              )}
            >
              All Customers
            </button>
            {availableCustomers.map(customer => (
              <button
                key={customer.id}
                onClick={() => onCustomerFilterChange?.(customer.id)}
                className={cn(
                  "w-full text-left px-2 py-1 rounded text-sm transition-colors",
                  customerFilter === customer.id 
                    ? "bg-blue-500/20 text-blue-300" 
                    : "text-slate-300 hover:bg-slate-700/30"
                )}
              >
                {customer.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer for dropdown mode */}
      {!expanded && (
        <div className="flex items-center justify-between pt-2 border-t border-slate-600/30">
          {activeFilterCount > 0 && (
            <button
              onClick={onClearAll}
              className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Clear all
            </button>
          )}
          <button
            onClick={onClose}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default CalendarFilters;